// src/journey/GoalEditorModal.tsx
//
// BekalNanti — Goal / Plan Editor & Creation Modal
// Clear mental model: explicit subject attribution ("Rencana ini untuk siapa?")
// Enables real-time editing, adding, and removing goals with inline Person creation.

import React, { useState, useEffect } from 'react'
import { GoalRecord, PersonRecord } from './types'
import { goalOptions } from '../onboarding/Onboarding'
import { formatRelationship } from '../lib/peopleService'
import { PersonModal } from '../people/PersonModal'
import {
  X,
  Check,
  Trash2,
  Plus,
  Loader2,
  Target,
  AlertCircle,
  Users,
  UserPlus,
} from 'lucide-react'

interface GoalEditorModalProps {
  isOpen: boolean
  goals: GoalRecord[]
  persons: PersonRecord[]
  householdId: string
  initialGoalId?: string | null
  initialPersonId?: string | null
  startInAddMode?: boolean
  onClose: () => void
  onUpdateGoal: (
    goalId: string,
    updates: {
      name: string
      goal_type: string
      priority: 'high' | 'medium' | 'low'
      person_id: string | null
    }
  ) => Promise<void>
  onAddGoal: (
    householdId: string,
    goal: {
      name: string
      goal_type: string
      priority: 'high' | 'medium' | 'low'
      person_id: string | null
    }
  ) => Promise<void>
  onDeleteGoal: (goalId: string) => Promise<void>
  onRefreshPersons?: () => Promise<void>
}

export const GoalEditorModal: React.FC<GoalEditorModalProps> = ({
  isOpen,
  goals,
  persons,
  householdId,
  initialGoalId,
  initialPersonId,
  startInAddMode = false,
  onClose,
  onUpdateGoal,
  onAddGoal,
  onDeleteGoal,
  onRefreshPersons,
}) => {
  const [selectedGoal, setSelectedGoal] = useState<GoalRecord | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [goalType, setGoalType] = useState<string>('education')
  const [goalName, setGoalName] = useState<string>('')
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('medium')
  const [personId, setPersonId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState(false)

  // Initialize or reset when modal opens or initialGoalId/initialPersonId changes
  useEffect(() => {
    if (!isOpen) return

    if (startInAddMode || (!initialGoalId && goals.length === 0)) {
      handleStartAddNew(initialPersonId)
    } else if (initialGoalId) {
      const found = goals.find((g) => g.id === initialGoalId)
      if (found) {
        handleSelectGoalToEdit(found)
      } else {
        handleStartAddNew(initialPersonId)
      }
    } else if (goals.length > 0) {
      handleSelectGoalToEdit(goals[0]!)
    } else {
      handleStartAddNew(initialPersonId)
    }
  }, [isOpen, initialGoalId, initialPersonId, startInAddMode])

  if (!isOpen) return null

  const handleSelectGoalToEdit = (g: GoalRecord) => {
    setSelectedGoal(g)
    setIsAddingNew(false)
    setGoalType(g.goalType)
    setGoalName(g.name)
    setPriority(g.priority)
    setPersonId(g.personId || '')
    setErrorMsg(null)
    setSuccessMsg(null)
  }

  const handleStartAddNew = (prefillPersonId?: string | null) => {
    setIsAddingNew(true)
    setSelectedGoal(null)
    const defaultCatalog = goalOptions[0] || { type: 'education', title: 'Pendidikan' }
    setGoalType(defaultCatalog.type)
    setGoalName(defaultCatalog.title)
    setPriority('medium')
    setPersonId(prefillPersonId !== undefined ? (prefillPersonId || '') : (persons[0]?.id || ''))
    setErrorMsg(null)
    setSuccessMsg(null)
  }

  const handleGoalTypeChange = (type: string) => {
    setGoalType(type)
    const catalogItem = goalOptions.find((g) => g.type === type)
    if (catalogItem) {
      setGoalName(catalogItem.title)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!goalName.trim()) {
      setErrorMsg('Nama rencana tidak boleh kosong.')
      return
    }

    setLoading(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      if (isAddingNew) {
        await onAddGoal(householdId, {
          name: goalName.trim(),
          goal_type: goalType,
          priority,
          person_id: personId || null,
        })
        setSuccessMsg('Rencana baru berhasil ditambahkan!')
        setIsAddingNew(false)
      } else if (selectedGoal) {
        await onUpdateGoal(selectedGoal.id, {
          name: goalName.trim(),
          goal_type: goalType,
          priority,
          person_id: personId || null,
        })
        setSuccessMsg('Perubahan rencana berhasil disimpan!')
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal menyimpan perubahan rencana.')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (goalId: string) => {
    if (goals.length <= 1) {
      setErrorMsg('Perencanaan memerlukan setidaknya satu rencana aktif.')
      return
    }

    setLoading(true)
    setErrorMsg(null)
    try {
      await onDeleteGoal(goalId)
      setSuccessMsg('Rencana berhasil dihapus.')
      const remaining = goals.filter((g) => g.id !== goalId)
      if (remaining.length > 0 && remaining[0]) {
        handleSelectGoalToEdit(remaining[0])
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal menghapus rencana.')
    } finally {
      setLoading(false)
    }
  }

  const handlePersonAddedSuccess = async () => {
    if (onRefreshPersons) {
      await onRefreshPersons()
    }
    setIsAddPersonModalOpen(false)
  }

  const activePersons = persons.filter((p) => p.status !== 'archived')

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="goal-editor-title"
      >
        <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-slate-200/90 overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center">
                <Target size={18} />
              </div>
              <div>
                <h2 id="goal-editor-title" className="text-base font-bold text-slate-900 leading-tight">
                  {isAddingNew ? 'Tambah Rencana Baru' : 'Kelola Rencana Bekal'}
                </h2>
                <p className="text-[11px] text-slate-500">
                  Tentukan penerima rencana, kategori, dan prioritas
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              aria-label="Tutup"
            >
              <X size={18} />
            </button>
          </div>

          {/* Content Body */}
          <div className="p-5 overflow-y-auto space-y-4 text-left">
            {/* Goal List Pills */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Daftar Rencana ({goals.length})
                </span>
                <button
                  type="button"
                  onClick={() => handleStartAddNew(initialPersonId)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#0EA5A4] hover:text-[#0F766E] cursor-pointer"
                >
                  <Plus size={13} />
                  <span>Tambah Rencana</span>
                </button>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {goals.map((g) => {
                  const isActive = !isAddingNew && selectedGoal?.id === g.id
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => handleSelectGoalToEdit(g)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                        isActive
                          ? 'bg-[#0EA5A4] text-white border-[#0EA5A4] shadow-2xs'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {g.name}
                    </button>
                  )
                })}
                {isAddingNew && (
                  <span className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-300">
                    + Rencana Baru
                  </span>
                )}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSave} className="space-y-4 pt-2 border-t border-slate-100">
              {/* Feedback Notifications */}
              {errorMsg && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
              {successMsg && (
                <div className="p-3 rounded-xl bg-teal-50 border border-teal-200 text-[#0F766E] text-xs flex items-center gap-2">
                  <Check size={14} className="flex-shrink-0" />
                  <span>{successMsg}</span>
                </div>
              )}

              {/* Explicit Plan Subject Attribution: "Rencana ini untuk siapa?" */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-700">
                    Rencana Ini Untuk Siapa?
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsAddPersonModalOpen(true)}
                    className="text-[#0EA5A4] hover:text-[#0F766E] text-xs font-semibold inline-flex items-center gap-1 cursor-pointer"
                  >
                    <UserPlus size={12} />
                    <span>+ Tambah Orang Baru</span>
                  </button>
                </div>

                {/* Person Choice Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {activePersons.map((p) => {
                    const isSelected = personId === p.id
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPersonId(p.id)}
                        className={`p-2.5 rounded-2xl border text-left transition-all flex items-center gap-2 cursor-pointer ${
                          isSelected
                            ? 'border-[#0EA5A4] bg-teal-50/50 ring-2 ring-teal-500/20 shadow-2xs'
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <div
                          className={`w-7 h-7 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                            isSelected
                              ? 'bg-[#0EA5A4] text-white'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {p.fullName.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-900 truncate">{p.fullName}</p>
                          <p className="text-[10px] text-slate-500 truncate">
                            {formatRelationship(p.relationship)}
                          </p>
                        </div>
                      </button>
                    )
                  })}

                  {/* General Family / Joint Option */}
                  <button
                    type="button"
                    onClick={() => setPersonId('')}
                    className={`p-2.5 rounded-2xl border text-left transition-all flex items-center gap-2 cursor-pointer ${
                      personId === ''
                        ? 'border-[#0EA5A4] bg-teal-50/50 ring-2 ring-teal-500/20 shadow-2xs'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                        personId === ''
                          ? 'bg-[#0EA5A4] text-white'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      <Users size={13} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-900 truncate">Keluarga Bersama</p>
                      <p className="text-[10px] text-slate-500 truncate">Umum / Rumah Tangga</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Category / Goal Type Selection */}
              <div className="space-y-1.5">
                <label htmlFor="goal-type-select" className="text-xs font-bold text-slate-700">
                  Kategori Rencana
                </label>
                <select
                  id="goal-type-select"
                  value={goalType}
                  onChange={(e) => handleGoalTypeChange(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
                >
                  {goalOptions.map((opt) => (
                    <option key={opt.id} value={opt.type}>
                      {opt.title} ({opt.englishLabel})
                    </option>
                  ))}
                </select>
              </div>

              {/* Goal Custom Title */}
              <div className="space-y-1.5">
                <label htmlFor="goal-name-input" className="text-xs font-bold text-slate-700">
                  Nama / Judul Rencana
                </label>
                <input
                  id="goal-name-input"
                  type="text"
                  value={goalName}
                  onChange={(e) => setGoalName(e.target.value)}
                  placeholder="Contoh: Kuliah S1 Kedokteran, DP Rumah, Dana Pensiun"
                  required
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
                />
              </div>

              {/* Priority Selector */}
              <div className="space-y-1.5">
                <span className="text-xs font-bold text-slate-700 block">Tingkat Prioritas</span>
                <div className="grid grid-cols-3 gap-2">
                  {(['high', 'medium', 'low'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      className={`py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer capitalize ${
                        priority === p
                          ? 'bg-teal-50 text-[#0F766E] border-[#0EA5A4] shadow-2xs'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {p === 'high' ? 'Tinggi' : p === 'low' ? 'Rendah' : 'Menengah'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions Footer */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <div>
                  {!isAddingNew && selectedGoal && (
                    <button
                      type="button"
                      onClick={() => handleDelete(selectedGoal.id)}
                      disabled={loading || goals.length <= 1}
                      title={
                        goals.length <= 1
                          ? 'Tidak dapat menghapus rencana satu-satunya'
                          : 'Hapus rencana ini'
                      }
                      className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs transition-all cursor-pointer disabled:opacity-50"
                  >
                    {loading && <Loader2 size={13} className="animate-spin" />}
                    <span>{isAddingNew ? 'Tambah Rencana' : 'Simpan Perubahan'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Inline Person Creation Modal */}
      {isAddPersonModalOpen && (
        <PersonModal
          isOpen={isAddPersonModalOpen}
          householdId={householdId}
          onClose={() => setIsAddPersonModalOpen(false)}
          onSuccess={handlePersonAddedSuccess}
        />
      )}
    </>
  )
}
